const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Now playing widget (decorative only — no real audio)
function initializeNowPlaying() {
  const root = document.querySelector('[data-pouff-now-playing]');
  if (!root) return;

  const openBtn = root.querySelector('[data-pouff-now-playing-open]');
  const closeBtn = root.querySelector('[data-pouff-now-playing-close]');
  const panel = root.querySelector('#pouff-now-playing-panel');
  if (!openBtn || !closeBtn || !panel) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setExpanded(expanded) {
    panel.hidden = !expanded;
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    openBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function expand() {
    if (!prefersReducedMotion && document.startViewTransition) {
      document.startViewTransition(() => setExpanded(true));
    } else {
      setExpanded(true);
    }
  }

  function collapse() {
    if (!prefersReducedMotion && document.startViewTransition) {
      document.startViewTransition(() => setExpanded(false));
    } else {
      setExpanded(false);
    }
  }

  openBtn.addEventListener('click', () => {
    const isExpanded = openBtn.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      collapse();
    } else {
      expand();
    }
  });

  closeBtn.addEventListener('click', () => {
    collapse();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openBtn.getAttribute('aria-expanded') === 'true') {
      collapse();
    }
  });
}

// Split-text wavy animation logic — targets wherever "Field Notes" itself
// renders: the not-yet-live pouff-blog-index masthead's <h1> (its "blog
// name" setting defaults to "Field Notes" directly), and the currently-live
// pouff-blog-header's small eyebrow label (its <h1> below it says "From the
// team.", which is NOT what this should animate). Only one of the two
// sections ever renders on a given page load.
const WAVY_TEXT_TARGET_SELECTOR = '.pb-masthead__name, .pouff-blog-header__eyebrow';
const WAVY_TEXT_SPLIT_CLASSNAME = 'pouff-wavy-text';
const WAVY_TEXT_CHAR_STAGGER_MS = 28;
const WAVY_TEXT_ARC_AMPLITUDE_PX = 10;

function buildWavyTextTree(text) {
  const wrapper = document.createElement('span');
  wrapper.className = WAVY_TEXT_SPLIT_CLASSNAME;
  wrapper.setAttribute('aria-label', text);

  const line = document.createElement('span');
  line.className = 'pouff-wavy-text__line';
  wrapper.appendChild(line);

  const words = text.split(' ');
  const totalChars = text.replace(/ /g, '').length;
  let charIndex = 0;

  words.forEach((word, wordIndex) => {
    const wordEl = document.createElement('span');
    wordEl.className = 'pouff-wavy-text__word';

    Array.from(word).forEach((char) => {
      const charEl = document.createElement('span');
      charEl.className = 'pouff-wavy-text__char';
      charEl.setAttribute('aria-hidden', 'true');
      charEl.textContent = char;

      const progress = totalChars > 1 ? charIndex / (totalChars - 1) : 0;
      const arcOffset = Math.round(Math.sin(progress * Math.PI) * WAVY_TEXT_ARC_AMPLITUDE_PX * -1);
      charEl.style.setProperty('--wavy-offset', `${arcOffset}px`);
      charEl.style.setProperty('--wavy-delay', `${charIndex * WAVY_TEXT_CHAR_STAGGER_MS}ms`);

      wordEl.appendChild(charEl);
      charIndex += 1;
    });

    line.appendChild(wordEl);

    if (wordIndex < words.length - 1) {
      const spaceEl = document.createElement('span');
      spaceEl.className = 'pouff-wavy-text__char pouff-wavy-text__space';
      spaceEl.setAttribute('aria-hidden', 'true');
      spaceEl.textContent = ' ';
      spaceEl.style.setProperty('--wavy-offset', '0px');
      spaceEl.style.setProperty('--wavy-delay', `${charIndex * WAVY_TEXT_CHAR_STAGGER_MS}ms`);
      line.appendChild(spaceEl);
      charIndex += 1;
    }
  });

  return wrapper;
}

function initializeWavyText(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const targets = Array.from(rootEl.querySelectorAll(WAVY_TEXT_TARGET_SELECTOR));
  if (targets.length === 0) return;

  targets.forEach((target) => {
    const originalText = target.textContent.trim();
    if (!originalText) return;

    target.innerHTML = '';
    target.appendChild(buildWavyTextTree(originalText));
  });
}

// Hero parallax logic — homepage hero only (.pouff-hero__media-frame drifts
// slower than scroll, .pouff-hero__text-inner drifts faster, via CSS vars
// --parallax-media-y / --parallax-text-y set on a throttled scroll listener).
function initializeHeroParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const heroSections = Array.from(document.getElementsByClassName('pouff-hero'));
  if (heroSections.length === 0) return;

  const MEDIA_DRIFT_MAX_PX = 24; // background layer: subtle, slower drift
  const TEXT_DRIFT_MAX_PX = 44; // foreground layer: slightly faster drift, creates depth

  heroSections.forEach((heroSection) => {
    const mediaFrame = heroSection.querySelector('.pouff-hero__media-frame');
    const textInner = heroSection.querySelector('.pouff-hero__text-inner');
    if (!mediaFrame && !textInner) return;

    let heroIsVisible = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        heroIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(heroSection);

    function updateHeroParallax() {
      const progress = percentageSeen(heroSection) / 100;
      if (mediaFrame) {
        mediaFrame.style.setProperty('--parallax-media-y', `${(-MEDIA_DRIFT_MAX_PX * progress).toFixed(2)}px`);
      }
      if (textInner) {
        textInner.style.setProperty('--parallax-text-y', `${(-TEXT_DRIFT_MAX_PX * progress).toFixed(2)}px`);
      }
    }

    function onHeroScroll() {
      if (!heroIsVisible) return;

      updateHeroParallax();
    }

    updateHeroParallax();
    window.addEventListener('scroll', throttle(onHeroScroll), { passive: true });
  });
}

// Material ripple — all buttons site-wide
const BUTTON_RIPPLE_SELECTOR = '.button, .pouff-btn, .pb-inline-cta__btn';

function initializeButtonRipple() {
  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest(BUTTON_RIPPLE_SELECTOR);
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const diameter = Math.sqrt(rect.width ** 2 + rect.height ** 2) * 2;

    const ripple = document.createElement('span');
    ripple.className = 'pouff-ripple';
    ripple.style.width = `${diameter}px`;
    ripple.style.height = `${diameter}px`;
    ripple.style.left = `${offsetX - diameter / 2}px`;
    ripple.style.top = `${offsetY - diameter / 2}px`;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ripple.classList.add('pouff-ripple--reduced-motion');
      button.appendChild(ripple);
      window.setTimeout(() => ripple.remove(), 120);
      return;
    }

    ripple.addEventListener('animationend', () => ripple.remove());
    button.appendChild(ripple);
  });
}

// "Fun Fact of the Day" PDP widget: expand/collapse + collision avoidance.
// Runs normally under Shopify.designMode (unlike the other animations in
// this file) so merchants can see/edit fact blocks live in the theme
// editor. rootEl defaults to document but accepts a section root element so
// it can be safely re-run on shopify:section:load without re-scanning the
// whole page.
function initializePdpFunFacts(rootEl = document) {
  const widgets = Array.from(rootEl.querySelectorAll('.pouff-fun-facts'));
  if (widgets.length === 0) return;

  widgets.forEach((widget) => {
    const peekButton = widget.querySelector('.pouff-fun-facts__peek');
    const panel = widget.querySelector('.pouff-fun-facts__panel');
    const collapseButton = widget.querySelector('.pouff-fun-facts__collapse');
    if (!peekButton || !panel || !collapseButton) return;

    // Show exactly one fact, re-rolled on every page view.
    //
    // Changed 2026-08-15. This used to persist the chosen index in
    // localStorage so a visitor kept the same fact forever, which is why the
    // widget appeared frozen: it was not a bug, it was the stored choice being
    // honoured on every subsequent load. Now the roll happens per view and
    // only the PREVIOUS index is remembered, purely to exclude it, so two
    // consecutive views can never show the same fact.
    //
    // This has to stay client-side. Picking in Liquid would bake the choice
    // into the cached HTML, so every visitor served that cache node would see
    // the same fact until it turned over, which is the same symptom again with
    // a slower clock.
    const peekText = widget.querySelector('.pouff-fun-facts__peek-text');
    const factItems = Array.from(widget.querySelectorAll('.pouff-fun-facts__item'));
    if (factItems.length > 0) {
      const storageKey = 'pouffFunFactLastIndex';
      const legacyKey = 'pouffFunFactIndex';
      let lastIndex = NaN;
      try {
        lastIndex = parseInt(window.localStorage.getItem(storageKey), 10);
        // Left behind by the pinned-choice version above. Removed so it does
        // not sit in visitors' storage forever meaning nothing.
        window.localStorage.removeItem(legacyKey);
      } catch (e) {}

      let pool = factItems.map((_, index) => index);
      if (pool.length > 1 && Number.isInteger(lastIndex)) {
        pool = pool.filter((index) => index !== lastIndex);
      }
      const chosenIndex = pool[Math.floor(Math.random() * pool.length)];

      try {
        window.localStorage.setItem(storageKey, String(chosenIndex));
      } catch (e) {}

      factItems.forEach((item, index) => {
        item.hidden = index !== chosenIndex;
      });

      if (peekText) {
        peekText.textContent = factItems[chosenIndex].textContent.trim();
      }
    }

    function expand() {
      widget.classList.add('pouff-fun-facts--expanded');
      peekButton.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      collapseButton.focus();
    }

    function collapse() {
      widget.classList.remove('pouff-fun-facts--expanded');
      peekButton.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
      peekButton.focus();
    }

    peekButton.addEventListener('click', () => {
      if (widget.classList.contains('pouff-fun-facts--expanded')) {
        collapse();
      } else {
        expand();
      }
    });

    collapseButton.addEventListener('click', collapse);

    widget.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && widget.classList.contains('pouff-fun-facts--expanded')) {
        collapse();
      }
    });

    // Collision avoidance: .pouff-mpdp__sizes (size pills), .pouff-mpdp__buybox
    // (add-to-cart button + Appstle's relocated subscription tooltip), and
    // .pouff-mpdp__soon (the "coming soon" waitlist form that replaces the
    // buybox for gated products) all render in normal document flow in
    // pouff-pdp-multi-size.liquid, not fixed/sticky, so any of them can
    // scroll into this widget's fixed bottom-right corner. Fade the widget
    // out of the way whenever any of them is on screen.
    //
    // .pouff-stickybuy added 2026-09-08. The three selectors above are all in
    // normal flow, so the widget yielded exactly when the buy box was on
    // screen -- and the mobile sticky bar is shown precisely when the real
    // Add to cart is OFF screen. The two were therefore visible under
    // complementary conditions and never both accounted for, so the widget sat
    // over the purchase bar for the whole of the page where the bar is the
    // only CTA. Observing it here works because the bar hides via
    // transform: translateY(104%), which moves it out of the viewport
    // geometrically, so IntersectionObserver reports it correctly; a plain
    // opacity or visibility hide would not have been observable this way.
    const collisionTargets = Array.from(
      document.querySelectorAll('.pouff-mpdp__sizes, .pouff-mpdp__buybox, .pouff-mpdp__soon, .pouff-stickybuy')
    );

    if (collisionTargets.length > 0 && 'IntersectionObserver' in window) {
      const intersecting = new Set();
      const collisionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersecting.add(entry.target);
          } else {
            intersecting.delete(entry.target);
          }
        });
        widget.classList.toggle('pouff-fun-facts--yield', intersecting.size > 0);
      });

      collisionTargets.forEach((target) => collisionObserver.observe(target));
    }
  });
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeWavyText();
  initializeHeroParallax();
  initializeButtonRipple();
  initializePdpFunFacts();
  initializeNowPlaying();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
  document.addEventListener('shopify:section:load', (event) => initializeWavyText(event.target));
  document.addEventListener('shopify:section:load', (event) => initializePdpFunFacts(event.target));
}
